import { Stack } from "@mui/material";
import { motion } from "framer-motion";

// ChristmasLoader.jsx
// Default export: a small, reusable, Tailwind-styled React loader with festive animations.
// Props:
//  - size (number) pixels for the square container (default 120)
//  - sparkle (boolean) whether to show sparkles (default true)

export default function ChristmasLoader({ size = 220, sparkle = true }) {
  const box = size;
  const circleR = Math.round(box * 0.28);
  const ornamentVariants = {
    bob: {
      y: [0, -6, 0],
      transition: {
        duration: 1.6,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      },
    },
    spin: {
      rotate: [0, 15, 0, -15, 0],
      transition: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
    },
  };

  const snowVariant = {
    fall: (i: any) => ({
      y: [-10, size * 0.6 + 10],
      x: [0, (i % 2 === 0 ? -1 : 1) * 8],
      opacity: [1, 0],
      transition: {
        duration: 2 + i * 0.6,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        delay: i * 0.2,
      },
    }),
  };

  const sparkles = sparkle ? Array.from({ length: 6 }) : [];
  const snowflakes = Array.from({ length: 5 });

  return (
    <Stack
      height={"100%"}
      direction="row"
      justifyContent="center"
      alignItems="center"
      className={"has-text-centered my-5"}
      spacing={0}
    >
      {/* Ornament (centerpiece) */}
      <motion.svg
        width={box * 0.7}
        height={box * 0.7}
        viewBox={`0 0 ${box} ${box}`}
        className="drop-shadow-2xl"
        variants={ornamentVariants as any}
        animate={["bob", "spin"]}
        style={{ originX: "50%", originY: "40%" }}
      >
        {/* string */}
        <line
          x1={box / 2}
          y1={8}
          x2={box / 2}
          y2={box * 0.24}
          stroke="#d1d5db"
          strokeWidth={1.5}
          strokeLinecap="round"
          opacity={0.6}
        />

        {/* top cap */}
        <rect
          x={box * 0.44}
          y={box * 0.22}
          width={box * 0.12}
          height={box * 0.06}
          rx={2}
          fill="#b91c1c"
        />

        {/* ornament body */}
        <defs>
          <radialGradient id="g1" cx="30%" cy="30%">
            <stop offset="0%" stopColor="#fff1f2" stopOpacity="0.9" />
            <stop offset="40%" stopColor="#f43f5e" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#8b0216" stopOpacity="1" />
          </radialGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <motion.circle
          cx={box / 2}
          cy={box / 2.3}
          r={circleR}
          fill="url(#g1)"
          filter="url(#glow)"
          initial={{ scale: 0.98 }}
          animate={{ scale: [0.98, 1.02, 0.99] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          stroke="#fff3f4"
          strokeWidth={0.6}
          opacity={0.98}
        />

        {/* white shine */}
        <motion.ellipse
          cx={box * 0.62}
          cy={box * 0.37}
          rx={circleR * 0.42}
          ry={circleR * 0.22}
          fill="#ffffff"
          opacity={0.28}
          animate={{ opacity: [0.28, 0.6, 0.28] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        />

        {/* little snow on ornament */}
        <g>
          <motion.circle
            cx={box * 0.4}
            cy={box * 0.45}
            r={2.2}
            fill="#fff"
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
          <motion.circle
            cx={box * 0.46}
            cy={box * 0.6}
            r={1.6}
            fill="#fff"
            animate={{ y: [0, -1.4, 0] }}
            transition={{ duration: 1.1, repeat: Infinity, delay: 0.2 }}
          />
        </g>

        {/* small holly leaf accent */}
        <path
          d={`M${box * 0.3} ${box * 0.33} C ${box * 0.25} ${box * 0.28}, ${
            box * 0.18
          } ${box * 0.28}, ${box * 0.22} ${box * 0.36}`}
          fill="#064e3b"
          opacity={0.95}
        />
      </motion.svg>

      {/* sparkles */}
      {sparkles.map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0], scale: [0.6, 1, 0.6] }}
          transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.2 }}
          style={{
            position: "absolute",
            left: `${10 + i * 12}%`,
            top: `${6 + (i % 2) * 6}%`,
          }}
          aria-hidden
        >
          <svg width={10} height={10} viewBox="0 0 24 24">
            <path
              d="M12 2 L13.8 8.5 L20.8 10 L13.8 11.5 L12 18 L10.2 11.5 L3.2 10 L10.2 8.5 Z"
              fill="#fef3c7"
              opacity={0.95}
            />
          </svg>
        </motion.div>
      ))}

      {/* snowflakes (falling) */}
      {snowflakes.map((_, i) => (
        <motion.div
          key={`s${i}`}
          custom={i}
          variants={snowVariant as any}
          animate="fall"
          style={{ position: "absolute", left: `${12 + i * 14}%`, top: -8 }}
        >
          <svg width={12} height={12} viewBox="0 0 24 24" aria-hidden>
            <path
              d="M12 2 L12 6 M12 18 L12 22 M4 4 L8 8 M16 16 L20 20 M20 4 L16 8 M8 16 L4 20"
              stroke="#93c5fd"
              strokeWidth={1.2}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={0.9}
            />
          </svg>
        </motion.div>
      ))}
    </Stack>
  );
}
